import React from "react";
import { Link } from "react-router-dom";
import {
  Rocket, Award, Trophy, BookOpen, Megaphone, Calendar, Globe2, DollarSign,
  Target, ArrowRight,
} from "lucide-react";

/**
 * Phase 14 — Lightweight stub pages for new dashboard sidebar items.
 * Each page renders the LATM cosmic shell and links to real existing features
 * so nothing 404s. They will be enriched later as data flows in.
 */

function StubShell({ icon: Icon, eyebrow, title, intro, sections = [], children, testId }) {
  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid={testId}>
      <header>
        <div className="eyebrow flex items-center gap-2"><Icon className="h-3 w-3" /> {eyebrow}</div>
        <h1 className="font-anton text-3xl sm:text-4xl uppercase mt-2 text-white">{title}</h1>
        <p className="text-lifted-dim mt-3 max-w-2xl">{intro}</p>
      </header>
      {sections.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sections.map((s) => (
            <Link
              key={s.label}
              to={s.to}
              className="glass-panel p-5 hover:-translate-y-1 transition-transform"
              data-testid={`stub-link-${s.label.toLowerCase().replace(/\s+/g,"-")}`}
            >
              <div className="font-anton text-lg uppercase">{s.label}</div>
              <p className="text-[11px] text-lifted-dim mt-1">{s.blurb}</p>
              <div className="text-[10px] uppercase tracking-wider text-lifted-green mt-3 inline-flex items-center gap-1">
                Open <ArrowRight size={10} />
              </div>
            </Link>
          ))}
        </div>
      )}
      {children}
    </div>
  );
}

export function LaunchpadPage() {
  return (
    <StubShell
      testId="launchpad-page" icon={Rocket}
      eyebrow="Your journey starts here"
      title="Launchpad"
      intro="One screen, every tool. Start with training, share your first product link, and check the ecosystem feed for what teammates are doing right now."
      sections={[
        { label: "Training Center", to: "/training", blurb: "Role-specific modules and certifications." },
        { label: "Product Links", to: "/affiliate/links", blurb: "Generate tracked links for any product." },
        { label: "Marketing Tools", to: "/affiliate/marketing", blurb: "Creative kit, copy, and graphics." },
        { label: "Lifted Network", to: "/landing-pad", blurb: "Announcements + threaded team discussion." },
        { label: "Achievements", to: "/achievements", blurb: "Unlocks earned as you contribute." },
        { label: "Leaderboard", to: "/leaderboard", blurb: "Top performers across the ecosystem." },
      ]}
    />
  );
}

export function EcosystemMapPage() {
  return (
    <StubShell
      testId="ecosystem-map-page" icon={Globe2}
      eyebrow="Geographic ecosystem"
      title="Ecosystem Map"
      intro="Lifted partners, drivers, franchises, and active delivery zones across the network. Live map coordinates require a map provider — until then we surface the counts and the partner/franchise/driver lists you can drill into."
      sections={[
        { label: "Partner Stores",   to: "/admin/partners",      blurb: "All approved partner stores in the network." },
        { label: "Franchises",       to: "/admin/franchises",    blurb: "Franchise territories and operators." },
        { label: "Delivery Zones",   to: "/admin/locations",     blurb: "Active same-day delivery areas." },
        { label: "Drivers Roster",   to: "/admin/delivery/drivers", blurb: "Driver list with status flags." },
      ]}
    />
  );
}

export function EventsPage() {
  return (
    <StubShell
      testId="events-page" icon={Calendar}
      eyebrow="Lifted calendar"
      title="Events & Webinars"
      intro="Weekly ecosystem calls, training sessions, and Launchpad live reveals. Subscribe to the Lifted Landing Pad for reminders."
    >
      <ul className="glass-panel divide-y divide-white/5">
        {[
          { d: "JUN 10", t: "Ecosystem Call",     s: "Weekly team call — 10:00 AM EST" },
          { d: "JUN 14", t: "Training: Level Up", s: "Advanced strategies — 2:00 PM EST" },
          { d: "JUN 20", t: "Launchpad Live",     s: "New features reveal — 7:00 PM EST" },
        ].map((e) => (
          <li key={e.t} className="p-4 flex items-center gap-4">
            <div className="w-16 text-center">
              <div className="font-anton text-lifted-green text-lg">{e.d}</div>
            </div>
            <div className="flex-1">
              <div className="font-anton text-white uppercase">{e.t}</div>
              <div className="text-xs text-lifted-dim">{e.s}</div>
            </div>
          </li>
        ))}
      </ul>
    </StubShell>
  );
}

export function AchievementsPage() {
  const achievements = [
    { name: "First Referral", desc: "Refer your first new customer.", unlocked: false },
    { name: "First Sale",     desc: "Close your first commissioned sale.", unlocked: false },
    { name: "Training Complete", desc: "Finish every module in your role's track.", unlocked: false },
    { name: "Top Performer",  desc: "Land in the top 10 leaderboard for the month.", unlocked: false },
    { name: "Team Builder",   desc: "Refer 5 affiliates, drivers, or partners.", unlocked: false },
    { name: "Product Promoter", desc: "Generate 100 clicks on tracked links.", unlocked: false },
  ];
  return (
    <StubShell
      testId="achievements-page" icon={Award}
      eyebrow="Recognition"
      title="Achievements"
      intro="Unlock badges as you contribute to the ecosystem. Real progress will populate here as Stripe and tracked-link data flow in."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {achievements.map((a) => (
          <div
            key={a.name}
            className={`glass-panel p-5 border ${a.unlocked ? "border-lifted-green/40" : "border-white/10"}`}
            data-testid={`achievement-${a.name.toLowerCase().replace(/\s+/g,"-")}`}
          >
            <Trophy size={18} className={a.unlocked ? "text-lifted-green" : "text-white/30"} />
            <div className="font-anton text-white mt-2">{a.name}</div>
            <div className="text-[11px] text-lifted-dim mt-1">{a.desc}</div>
            <div className="text-[10px] uppercase tracking-wider mt-3">
              {a.unlocked
                ? <span className="text-lifted-green">Unlocked</span>
                : <span className="text-lifted-dim">Locked</span>}
            </div>
          </div>
        ))}
      </div>
    </StubShell>
  );
}

export function LeaderboardPage() {
  return (
    <StubShell
      testId="leaderboard-page" icon={Trophy}
      eyebrow="Top of the ecosystem"
      title="Leaderboard"
      intro="Real performance rankings across affiliates, drivers, partners, and franchise territories. Populates from the affiliate commission ledger and delivery completion data."
      sections={[
        { label: "Top Affiliates",   to: "/admin/affiliate/ledger",  blurb: "Highest commission earners this month." },
        { label: "Top Drivers",      to: "/admin/delivery/drivers",  blurb: "Most deliveries completed." },
        { label: "Top Partners",     to: "/admin/partners",          blurb: "Partner stores by order volume." },
        { label: "Top Franchises",   to: "/admin/franchises",        blurb: "Best-performing territories." },
      ]}
    />
  );
}

export function ResourcesPage() {
  return (
    <StubShell
      testId="resources-page" icon={BookOpen}
      eyebrow="Documentation"
      title="Resources"
      intro="Brand guidelines, FAQ, compliance docs, and onboarding guides for every Lifted ecosystem role."
      sections={[
        { label: "Affiliate Playbook", to: "/affiliate-zone", blurb: "How affiliates win in the ecosystem." },
        { label: "Driver Handbook",    to: "/training",       blurb: "Same-day delivery best practices." },
        { label: "Partner Guide",      to: "/partner",        blurb: "Setting up your store inside Lifted." },
        { label: "Franchise FAQ",      to: "/franchise",      blurb: "How to operate a Lifted territory." },
        { label: "Brand Assets",       to: "/admin/brand-assets", blurb: "Logos, copy, color palette." },
        { label: "Contracts",          to: "/dashboard/contracts", blurb: "Standard ecosystem agreements." },
      ]}
    />
  );
}

export function MarketingToolsPage() {
  return (
    <StubShell
      testId="marketing-tools-page" icon={Megaphone}
      eyebrow="For affiliates"
      title="Marketing Tools"
      intro="Tracked product links, copy snippets, and graphics for affiliates to share across content channels."
      sections={[
        { label: "Product Links",      to: "/affiliate/links",   blurb: "Generate tracked links for any product." },
        { label: "Sales / Commissions", to: "/affiliate/sales",   blurb: "See your conversions and earnings." },
        { label: "Training",           to: "/training",          blurb: "How to share and promote compliantly." },
        { label: "Affiliate Zone",     to: "/affiliate-zone",    blurb: "Public-facing recruitment page." },
      ]}
    />
  );
}

export function AdminFinances() {
  return (
    <StubShell
      testId="admin-finances-page" icon={DollarSign}
      eyebrow="Ecosystem money"
      title="Finances"
      intro="Total revenue, payouts, commissions, and partner settlements. Real numbers unlock once Stripe is connected."
      sections={[
        { label: "Revenue Dashboard", to: "/reports",            blurb: "Cross-module financial rollup." },
        { label: "Affiliate Payouts", to: "/admin/affiliate/payouts", blurb: "Per-affiliate payout ledger." },
        { label: "Partner Settlements", to: "/admin/partners",   blurb: "Partner store payouts." },
        { label: "Driver Earnings",   to: "/admin/delivery",     blurb: "Per-trip + tip ledger." },
      ]}
    />
  );
}

export function AffiliateOpportunities() {
  return (
    <StubShell
      testId="affiliate-opportunities-page" icon={Target}
      eyebrow="Earn paths"
      title="Opportunities"
      intro="Bonus campaigns, seasonal promos, and high-yield product pushes. Drops here as the ecosystem grows."
    />
  );
}
